import axios from 'axios';
import { BACKEND_API } from '../configs/constants';
import { Agent } from 'https';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { JWT_SESSION_STORAGE } from '../app/atoms/Auth/SignInUp/SignInUp';
import { setupCache } from 'axios-cache-adapter';

export function getToken() {
  return localStorage.getItem(JWT_SESSION_STORAGE);
}

// Create `axios-cache-adapter` instance
const cache = setupCache({
  maxAge: 15 * 60 * 1000
});

export const axiosInstance = axios.create({
  baseURL: BACKEND_API,
  httpsAgent: new Agent({
    rejectUnauthorized: false
  }),
  adapter: cache.adapter,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'multipart/form-data',
    'Access-Control-Allow-Origin': '*'
  }
});

axiosInstance.interceptors.request.use(
  config => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${getToken()}`;
    }
    return config;
  },
  error => {
    Promise.reject(error);
  }
);

export const useAxiosLoader = () => {
  const [counter, setCounter] = useState(0);
  const inc = useCallback(() => setCounter(counter => counter + 1), [setCounter]); // add to counter
  const dec = useCallback(() => setCounter(counter => counter - 1), [setCounter]); // remove from counter

  const interceptors = useMemo(
    () => ({
      request: (config: any) => (inc(), config),
      response: (response: any) => (dec(), response),
      error: (error: any) => (dec(), Promise.reject(error))
    }),
    [inc, dec]
  ); // create the interceptors

  useEffect(() => {
    // add request interceptors
    const reqInterceptor = axiosInstance.interceptors.request.use(interceptors.request, interceptors.error);
    // add response interceptors
    const resInterceptor = axiosInstance.interceptors.response.use(interceptors.response, interceptors.error);
    return () => {
      // remove all intercepts when done
      axiosInstance.interceptors.request.eject(reqInterceptor);
      axiosInstance.interceptors.response.eject(resInterceptor);
    };
  }, [interceptors]);

  return [counter > 0];
};

export const publicURL = process.env.PUBLIC_URL;
